import React, { useState, useEffect } from 'react';
import { Card, Typography, Button, Alert, Spin, Input, Space, message, Tabs } from 'antd';
import { CopyOutlined, KeyOutlined, DesktopOutlined, CodeOutlined } from '@ant-design/icons';
import { getApiKeyInfo } from '../services/api';

const { Title, Text, Paragraph } = Typography;

const ApiKeyConfig = () => {
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');

  useEffect(() => {
    fetchApiKey();
  }, []);

  const fetchApiKey = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getApiKeyInfo();
      if (res.status === 'success' && res.data) {
        setApiKey(res.data.api_key);
      } else {
        setError('获取 API Key 失败');
      }
    } catch (e) {
      setError(e.message || '获取 API Key 失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      message.success('已复制');
      setTimeout(() => setCopied(''), 2000);
    });
  };

  const baseUrl = window.location.origin;
  const mcpUrl = `${baseUrl}/mcp/`;

  const mcpConfigWithKey = (client) => {
    if (client === 'claude') {
      return JSON.stringify({
        mcpServers: {
          "appointment-booking": {
            url: `${mcpUrl}?api_key=${apiKey}`,
          },
        },
      }, null, 2);
    }
    if (client === 'cursor') {
      return JSON.stringify({
        mcpServers: {
          "appointment-booking": {
            url: `${mcpUrl}?api_key=${apiKey}`,
          },
        },
      }, null, 2);
    }
    if (client === 'vscode') {
      return JSON.stringify({
        mcp: {
          servers: {
            "appointment-booking": {
              url: `${mcpUrl}?api_key=${apiKey}`,
              type: "sse",
            },
          },
        },
      }, null, 2);
    }
    return '';
  };

  const tabItems = [
    {
      key: 'claude',
      label: <span><DesktopOutlined /> Claude Desktop</span>,
      children: (
        <div className="mcp-config-section">
          <Paragraph>将以下配置添加到 Claude Desktop 配置文件中：</Paragraph>
          <Text type="secondary" className="mcp-config-path">
            macOS: <code>~/Library/Application Support/Claude/claude_desktop_config.json</code><br />
            Windows: <code>%APPDATA%\Claude\claude_desktop_config.json</code>
          </Text>
          <div className="mcp-config-code">
            <Button
              size="small"
              icon={<CopyOutlined />}
              type={copied === 'claude-apikey' ? 'primary' : 'default'}
              onClick={() => handleCopy(mcpConfigWithKey('claude'), 'claude-apikey')}
              className="mcp-config-copy"
            >
              {copied === 'claude-apikey' ? '已复制' : '复制'}
            </Button>
            <pre className="mcp-config-pre">{mcpConfigWithKey('claude')}</pre>
          </div>
        </div>
      ),
    },
    {
      key: 'cursor',
      label: <span><CodeOutlined /> Cursor</span>,
      children: (
        <div className="mcp-config-section">
          <Paragraph>
            在 Cursor 项目根目录创建 <code>.cursor/mcp.json</code>，或通过 Settings → MCP → Add new MCP Server 添加：
          </Paragraph>
          <div className="mcp-config-code">
            <Button
              size="small"
              icon={<CopyOutlined />}
              type={copied === 'cursor-apikey' ? 'primary' : 'default'}
              onClick={() => handleCopy(mcpConfigWithKey('cursor'), 'cursor-apikey')}
              className="mcp-config-copy"
            >
              {copied === 'cursor-apikey' ? '已复制' : '复制'}
            </Button>
            <pre className="mcp-config-pre">{mcpConfigWithKey('cursor')}</pre>
          </div>
        </div>
      ),
    },
    {
      key: 'vscode',
      label: <span><CodeOutlined /> VS Code</span>,
      children: (
        <div className="mcp-config-section">
          <Paragraph>
            在 VS Code 的 <code>settings.json</code> 中添加以下配置：
          </Paragraph>
          <Text type="secondary" className="mcp-config-path">
            Command Palette → Preferences: Open User Settings (JSON)
          </Text>
          <div className="mcp-config-code">
            <Button
              size="small"
              icon={<CopyOutlined />}
              type={copied === 'vscode-apikey' ? 'primary' : 'default'}
              onClick={() => handleCopy(mcpConfigWithKey('vscode'), 'vscode-apikey')}
              className="mcp-config-copy"
            >
              {copied === 'vscode-apikey' ? '已复制' : '复制'}
            </Button>
            <pre className="mcp-config-pre">{mcpConfigWithKey('vscode')}</pre>
          </div>
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="apikey-config-page">
      <Card>
        <div className="mcp-guide-header">
          <div>
            <Title level={4} style={{ margin: 0 }}>API Key 配置</Title>
            <Text type="secondary">
              使用永久有效的 API Key 连接 AI 客户端，无需定期刷新 Token。
            </Text>
          </div>
          <KeyOutlined style={{ fontSize: 32, color: 'var(--primary-blue)', opacity: 0.6 }} />
        </div>

        {error ? (
          <Alert
            type="error"
            message="获取失败"
            description={error}
            style={{ marginTop: 16 }}
            action={
              <Button size="small" onClick={fetchApiKey}>重试</Button>
            }
          />
        ) : (
          <>
            <Alert
              type="warning"
              style={{ marginTop: 16, marginBottom: 24 }}
              message="请妥善保管此 Key"
              description="API Key 拥有完整的管理员权限，泄露将导致他人可以操控预约系统。请勿在公开场合分享。"
            />

            <div style={{ marginBottom: 24 }}>
              <Text strong>你的 API Key：</Text>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <Input.Password
                  value={apiKey}
                  readOnly
                  style={{ fontFamily: 'monospace', fontSize: 14 }}
                />
                <Button
                  icon={<CopyOutlined />}
                  type={copied === 'key' ? 'primary' : 'default'}
                  onClick={() => handleCopy(apiKey, 'key')}
                >
                  {copied === 'key' ? '已复制' : '复制'}
                </Button>
              </div>
            </div>

            <Title level={5}>使用方式</Title>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Card size="small" type="inner">
                <Text strong>方式一：Bearer Header（推荐用于 REST API）</Text>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <Input
                    readOnly
                    value={`Authorization: Bearer ${apiKey}`}
                    style={{ fontFamily: 'monospace', fontSize: 13 }}
                  />
                  <Button
                    icon={<CopyOutlined />}
                    onClick={() => handleCopy(`Authorization: Bearer ${apiKey}`, 'bearer')}
                  >
                    {copied === 'bearer' ? '已复制' : '复制'}
                  </Button>
                </div>
              </Card>

              <Card size="small" type="inner">
                <Text strong>方式二：Query 参数（推荐用于 MCP 客户端）</Text>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <Input
                    readOnly
                    value={`${mcpUrl}?api_key=${apiKey}`}
                    style={{ fontFamily: 'monospace', fontSize: 13 }}
                  />
                  <Button
                    icon={<CopyOutlined />}
                    onClick={() => handleCopy(`${mcpUrl}?api_key=${apiKey}`, 'query')}
                  >
                    {copied === 'query' ? '已复制' : '复制'}
                  </Button>
                </div>
              </Card>

              <Card size="small" type="inner">
                <Text strong>方式三：curl 测试</Text>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <Input
                    readOnly
                    value={`curl -H "Authorization: Bearer ${apiKey}" ${baseUrl}/api/get_tasks`}
                    style={{ fontFamily: 'monospace', fontSize: 13 }}
                  />
                  <Button
                    icon={<CopyOutlined />}
                    onClick={() => handleCopy(`curl -H "Authorization: Bearer ${apiKey}" ${baseUrl}/api/get_tasks`, 'curl')}
                  >
                    {copied === 'curl' ? '已复制' : '复制'}
                  </Button>
                </div>
              </Card>
            </Space>

            <Title level={5} style={{ marginTop: 24 }}>MCP 客户端一键配置</Title>
            <Paragraph type="secondary">
              以下配置已内置你的 API Key，无需额外替换 Token，可直接使用。
            </Paragraph>
            <Tabs items={tabItems} defaultActiveKey="claude" />
          </>
        )}
      </Card>
    </div>
  );
};

export default ApiKeyConfig;
